// Router + QueryClient (T01 §9.15). routeTree.gen.ts is generated by @tanstack/router-plugin on
// `vite dev`/`vite build`; never edit it and never import it anywhere else.
import { createRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { routeTree } from "../routeTree.gen";
import { reportNavigation } from "../telemetry";
import { RouteError } from "./route-error";
import { RoutePending } from "./route-pending";
import {
  retriesRead,
  setPasswordChangeHandler,
  setUnauthorizedHandler,
} from "../api/resilient-fetch";

// J-215: a read is asked again once, never after its 15-second limit or a 4xx answer
// (`retriesRead`), so no screen waits 30 s on a read that already failed.
export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: retriesRead, staleTime: 30_000 } },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
  // J-209 (inbox 06 PR D item 8): the app's own error page, never the router's default one.
  defaultErrorComponent: RouteError,
  // J-215 (inbox 22 item 1): a guard waiting on the server shows a status, never a blank page.
  defaultPendingComponent: RoutePending,
});

// One "navigation" span + log record per route change (T7 frontend interaction telemetry).
router.subscribe("onResolved", ({ toLocation }) => reportNavigation(toLocation.pathname));

// J-210 (inbox 06 PR E point 3): a 401 from the API is an ended session (the 12-hour cap, an idle
// expiry, a revocation, a sign-out in another tab). Before this the screens showed the raw 401 and
// `reason=expired` was never set. Now the user goes to the sign-in page once, with the page to come back
// to; the sign-in page clears the cache and router state (no PHI stays in memory, T12). J-243: clearing
// here, with the page still on screen, made its reads refetch without a session and cache their 401s.
let leaving = false;
setUnauthorizedHandler(() => {
  const here = router.state.location;
  if (leaving || here.pathname === "/login" || here.pathname === "/two-factor") return;
  leaving = true;
  void router
    .navigate({
      to: "/login",
      search: {
        reason: "expired",
        redirect: here.pathname === "/playground" ? undefined : here.href,
      },
    })
    .finally(() => {
      leaving = false;
    });
});

// J-244: an admin reset this account's password while it was signed in, so the server now refuses every
// call with 403 `password_change_required`. The cache is cleared and the user goes to the page that
// changes it.
setPasswordChangeHandler(() => {
  if (router.state.location.pathname === "/change-password") return;
  queryClient.clear();
  void router.navigate({ to: "/change-password" });
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
